import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Flame, Trophy, Mic, FileText, Lightbulb, X, Plus, Save, Loader2, Download, Users, User } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";
import AdminPanel from "@/components/AdminPanel";

export default function ProfileTab({ user }) {
  const [persona, setPersona] = useState({
    stage_name: "",
    comedy_style: "",
    influences: [],
    topics: [],
    audience: "",
    tone: "",
    bio: ""
  });
  const [milestones, setMilestones] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [newInfluence, setNewInfluence] = useState("");
  const [newTopic, setNewTopic] = useState("");
  
  const isAdmin = user?.is_admin === true;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [personaRes, statsRes] = await Promise.all([
          fetch(`${API}/persona`, { credentials: 'include' }),
          fetch(`${API}/stats/milestones`, { credentials: 'include' })
        ]);

        if (personaRes.ok) {
          const data = await personaRes.json();
          setPersona({
            stage_name: data.stage_name || "",
            comedy_style: data.comedy_style || "",
            influences: data.influences || [],
            topics: data.topics || [],
            audience: data.audience || "",
            tone: data.tone || "",
            bio: data.bio || ""
          });
        }

        if (statsRes.ok) {
          setMilestones(await statsRes.json());
        }
      } catch (error) {
        console.error('Failed to fetch profile data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSavePersona = async () => {
    setIsSaving(true);
    try {
      const response = await fetch(`${API}/persona`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(persona)
      });

      if (response.ok) {
        toast.success("Persona updated");
      } else {
        toast.error("Failed to save");
      }
    } catch (error) {
      toast.error("Failed to save persona");
    } finally {
      setIsSaving(false);
    }
  };

  const addInfluence = () => {
    const influence = newInfluence.trim();
    if (influence && !persona.influences.includes(influence)) {
      setPersona(prev => ({
        ...prev,
        influences: [...prev.influences, influence]
      }));
      setNewInfluence("");
    }
  };

  const removeInfluence = (inf) => {
    setPersona(prev => ({
      ...prev,
      influences: prev.influences.filter(i => i !== inf)
    }));
  };

  const addTopic = () => {
    const topic = newTopic.trim();
    if (topic && !persona.topics.includes(topic)) {
      setPersona(prev => ({
        ...prev,
        topics: [...prev.topics, topic]
      }));
      setNewTopic("");
    }
  };

  const removeTopic = (t) => {
    setPersona(prev => ({
      ...prev,
      topics: prev.topics.filter(topic => topic !== t)
    }));
  };

  const handleExportData = async () => {
    setIsExporting(true);
    try {
      // Fetch the file from the backend (server generates the text file directly)
      const response = await fetch(`${API}/export/all/download`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Export API error:', errorText);
        throw new Error('Export failed');
      }
      
      const text = await response.text();
      const filename = `BitStack_Export_${new Date().toISOString().split('T')[0]}.txt`;
      
      // Most reliable download: data URI approach (works in iframes, proxied envs)
      const dataUri = 'data:text/plain;charset=utf-8,' + encodeURIComponent(text);
      const a = document.createElement('a');
      a.href = dataUri;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
      }, 250);
      
      toast.success("Your data has been exported!");
    } catch (error) {
      console.error('Export error:', error);
      toast.error("Failed to export data. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const profileContent = (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-8">
      {/* Milestones */}
      <section>
        <h2 className="font-display text-xl uppercase tracking-wider mb-4">Your Journey</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <Flame className="w-6 h-6 text-primary mx-auto mb-2" />
              <p className="text-3xl font-display text-primary">{milestones?.writing_streak || 0}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Day Streak</p>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <Trophy className="w-6 h-6 text-amber-500 mx-auto mb-2" />
              <p className="text-3xl font-display text-amber-500">{milestones?.longest_streak || 0}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Best Streak</p>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <FileText className="w-6 h-6 text-blue-400 mx-auto mb-2" />
              <p className="text-3xl font-display text-blue-400">{milestones?.bits_in_rotation || 0}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">In Rotation</p>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <Mic className="w-6 h-6 text-green-400 mx-auto mb-2" />
              <p className="text-3xl font-display text-green-400">{milestones?.sets_performed || 0}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Sets Logged</p>
            </CardContent>
          </Card>
        </div>

        {/* Secondary Stats */}
        <div className="grid grid-cols-3 gap-4 mt-4">
          <div className="text-center py-3 bg-muted/30 rounded-sm">
            <p className="text-xl font-mono">{milestones?.total_bits || 0}</p>
            <p className="text-xs text-muted-foreground">Total Bits</p>
          </div>
          <div className="text-center py-3 bg-muted/30 rounded-sm">
            <p className="text-xl font-mono">{milestones?.ideas_captured || 0}</p>
            <p className="text-xs text-muted-foreground">Ideas Captured</p>
          </div>
          <div className="text-center py-3 bg-muted/30 rounded-sm">
            <p className="text-xl font-mono">{milestones?.ideas_developed || 0}</p>
            <p className="text-xs text-muted-foreground">Ideas → Bits</p>
          </div>
        </div>
      </section>

      {/* Persona */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl uppercase tracking-wider">Your Persona</h2>
          <Button 
            onClick={handleSavePersona} 
            disabled={isSaving}
            className="bg-primary text-primary-foreground"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save
          </Button>
        </div>

        <Card className="bg-card border-border">
          <CardContent className="p-6 space-y-6">
            {/* Stage Name */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Stage Name
              </Label>
              <Input
                placeholder="What do they call you on stage?"
                value={persona.stage_name}
                onChange={(e) => setPersona(prev => ({ ...prev, stage_name: e.target.value }))}
                className="bg-background/50 border-border"
              />
            </div>

            {/* Comedy Style & Tone Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                  Comedy Style
                </Label>
                <Input
                  placeholder="Observational, dark, absurdist, storytelling..."
                  value={persona.comedy_style}
                  onChange={(e) => setPersona(prev => ({ ...prev, comedy_style: e.target.value }))}
                  className="bg-background/50 border-border"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                  Tone
                </Label>
                <Input
                  placeholder="Edgy, clean, sarcastic, deadpan..."
                  value={persona.tone}
                  onChange={(e) => setPersona(prev => ({ ...prev, tone: e.target.value }))}
                  className="bg-background/50 border-border"
                />
              </div>
            </div>

            {/* Influences */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Influences
              </Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Add a comedian you admire"
                  value={newInfluence}
                  onChange={(e) => setNewInfluence(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addInfluence())}
                  className="bg-background/50 border-border"
                />
                <Button type="button" variant="outline" size="icon" onClick={addInfluence}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {persona.influences.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {persona.influences.map((inf) => (
                    <Badge key={inf} variant="secondary" className="gap-1">
                      {inf}
                      <button onClick={() => removeInfluence(inf)} className="hover:text-destructive">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Topics */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Topics You Work
              </Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Add a topic you often explore"
                  value={newTopic}
                  onChange={(e) => setNewTopic(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTopic())}
                  className="bg-background/50 border-border"
                />
                <Button type="button" variant="outline" size="icon" onClick={addTopic}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {persona.topics.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {persona.topics.map((topic) => (
                    <Badge key={topic} variant="outline" className="gap-1 border-primary/50 text-primary">
                      {topic}
                      <button onClick={() => removeTopic(topic)} className="hover:text-destructive">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Target Audience */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Target Audience
              </Label>
              <Input
                placeholder="Who are you writing for?"
                value={persona.audience}
                onChange={(e) => setPersona(prev => ({ ...prev, audience: e.target.value }))}
                className="bg-background/50 border-border"
              />
            </div>

            {/* Bio */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                About Your Comedy
              </Label>
              <Textarea
                placeholder="Describe your comedic voice, what you're working on, where you're headed..."
                value={persona.bio}
                onChange={(e) => setPersona(prev => ({ ...prev, bio: e.target.value }))}
                className="bg-background/50 border-border min-h-[120px] resize-none"
              />
              <p className="text-xs text-muted-foreground">
                This helps AI tools understand your voice when brainstorming.
              </p>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Data Export */}
      <section>
        <h2 className="font-display text-xl uppercase tracking-wider mb-4">Your Data</h2>
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold mb-1">Export Your Library</h3>
                <p className="text-sm text-muted-foreground">
                  Download all your bits, ideas, performances, and profile data in a readable text format.
                  <br />
                  <span className="text-xs">Your work is yours. Take it anywhere.</span>
                </p>
              </div>
              <Button 
                onClick={handleExportData}
                disabled={isExporting}
                variant="outline"
                className="gap-2"
                data-testid="export-all-data-button"
              >
                {isExporting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                {isExporting ? "Exporting..." : "Export All Data"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  );

  // If admin, show tabs with Profile and Admin sections
  if (isAdmin) {
    return (
      <Tabs defaultValue="profile" className="h-full">
        <div className="border-b border-border px-4">
          <TabsList className="h-10 bg-transparent">
            <TabsTrigger value="profile" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary gap-2">
              <User className="w-4 h-4" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="admin" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary gap-2">
              <Users className="w-4 h-4" />
              Admin
            </TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="profile" className="mt-0">
          {profileContent}
        </TabsContent>
        <TabsContent value="admin" className="mt-0 p-4 md:p-6">
          <AdminPanel />
        </TabsContent>
      </Tabs>
    );
  }

  return profileContent;
}
